/**
 * useProgramsModel — the Model hook for the unified Programs page.
 *
 * MVVM boundaries (hard rules, enforced in tests):
 *  - NO toasts, NO analytics, NO `console.*` inside this file.
 *  - NO JSX.
 *  - Mutation dispatchers return `{ok: true} | {ok: false, message}`. The
 *    ViewModel decides what to do with the outcome (toast, track, retry).
 *  - Optimistic state lives here via the helpers in `programs.optimistic.ts`.
 *    Rollback on error is keyed by request id, not by React closure age.
 *
 * Everything UI-shaped (toasts, analytics, retries, modal triggers) lives
 * in the ViewModel (Step 3).
 */
import { ApolloError, gql, useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useInfluencer } from '@client/new/common/LiteUser.mapper';
import { useDuplicateCollection } from '@client/new/pages/collections/collection.query';
import { useAsyncQueryRaw } from '@client/new/helpers/gql';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import {
  useArchiveCollectionMutation,
  useDeleteCollectionMutation,
  useOrderCollectionsMutation,
  useProgramsGetListQuery,
  usePublishCollectionMutation,
  SalesType as GqlSalesType,
} from 'generated/graphql';

import { toProgramsDomain } from './programs.mappers';
import {
  applyOptimisticOrder,
  buildReqId,
  clearStaleInflight,
  dropOverlayOnRefresh,
  emptyOptimisticState,
  settleInflight,
  startInflight,
} from './programs.optimistic';
import {
  DispatcherResult,
  DuplicateDispatcherResult,
  OptimisticState,
  ProgramItem,
  ProgramsDomain,
  ProgramsModel,
  ReorderPayload,
} from './programs.types';

// How long to keep settled inflight entries around before the janitor drops
// them. Long enough for the ViewModel to surface the error toast, short
// enough that a long-lived session doesn't accumulate them without bound.
const INFLIGHT_TTL_MS = 30_000;
const INFLIGHT_JANITOR_INTERVAL_MS = 60_000;

// Inline GraphQL documents for the convert-to-* actions. Matching `.graphql`
// files live alongside this module so codegen will pick them up once the
// schema endpoint is reachable; until then we drive these mutations manually
// via Apollo to avoid editing the generated bundle.
//
// The mutation response includes the same fields the list query selects so
// Apollo's normalized cache patches the row by id — the Status, Offers and
// other columns refresh without a follow-up list refetch.
const CONVERSION_RESULT_FIELDS = `
  id
  isVisible
  influencerId
  name
  accessType
  contentStructure
  publishingBehaviour
  isLocked
  archivedAt
  publishedAt
  index
  deeplinkUrl
  programBusinessModel
  mediaCover {
    cdn01
    image
  }
  exclusiveProgramMetadata {
    deeplinkUrl
    checkoutPageUrl
    price
  }
`;

export const PROGRAMS_CONVERT_TO_SUBSCRIPTION = gql`
  mutation ProgramsConvertToSubscription($programId: Int!) {
    convertToSubscriptionProgram(programId: $programId) {
      ${CONVERSION_RESULT_FIELDS}
    }
  }
`;

export const PROGRAMS_CONVERT_TO_UNASSIGNED = gql`
  mutation ProgramsConvertToUnassigned($programId: Int!) {
    convertToUnassignedProgram(programId: $programId) {
      ${CONVERSION_RESULT_FIELDS}
    }
  }
`;

type ConversionVars = { programId: number };

type ProgramsConvertToSubscriptionData = {
  convertToSubscriptionProgram: { id: number };
};

type ProgramsConvertToUnassignedData = {
  convertToUnassignedProgram: { id: number };
};

export type UseProgramsModelParams = {
  influencerId?: string;
};

export const useProgramsModel = (
  p: UseProgramsModelParams,
): Async<ProgramsModel> => {
  // ---- Normalize the influencer id once (guards against NaN / undefined) --
  const creatorId = toCreatorId(p.influencerId);
  const canFetch = creatorId !== null;

  // ---- Query --------------------------------------------------------------
  const query = useProgramsGetListQuery({
    variables: { influencerId: creatorId ?? 0 },
    skip: !canFetch,
    fetchPolicy: 'cache-and-network',
  });

  const { data: asyncDomain, silentRefetch } = useAsyncQueryRaw({
    query,
    mapper: toProgramsDomain({ creatorId: creatorId ?? 0 }),
  });

  const asyncInfluencer = useInfluencer(creatorId);
  const isCreatorLive =
    asyncInfluencer.type === 'fetched' && asyncInfluencer.value.isLive;

  const previousDomain = useMemo(
    () =>
      query.previousData
        ? toProgramsDomain({ creatorId: creatorId ?? 0 })(query.previousData)
        : null,
    [query.previousData, creatorId],
  );

  // Wrap silentRefetch in a helper that returns the shape our dispatchers
  // compose into their own DispatcherResult. On success we omit
  // `refreshFailed` so existing callers see `{ ok: true }` by structural
  // equality; on failure we propagate the message for the ViewModel to
  // toast while keeping the stale-but-visible program list intact.
  const refreshAfterMutation = useCallback(async (): Promise<{
    refreshFailed?: string;
  }> => {
    const result = await silentRefetch();
    if ('message' in result) return { refreshFailed: result.message };
    return {};
  }, [silentRefetch]);

  // ---- Optimistic reorder state ------------------------------------------
  const [optState, setOptState] =
    useState<OptimisticState>(emptyOptimisticState);
  // Stable ref for building request ids; avoids re-creating on every render.
  const nowRef = useRef<() => number>(() => Date.now());

  // Periodically prune settled inflight entries so long-lived sessions
  // don't accumulate them without bound.
  useEffect(() => {
    const now = nowRef.current;
    const handle = window.setInterval(() => {
      setOptState((s) => clearStaleInflight(s, now(), INFLIGHT_TTL_MS));
    }, INFLIGHT_JANITOR_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, []);

  // ---- Mutation hooks ----------------------------------------------------
  const [orderCollectionsMutation] = useOrderCollectionsMutation();
  const [archiveCollectionMutation] = useArchiveCollectionMutation();
  const [publishCollectionMutation] = usePublishCollectionMutation();
  const [deleteCollectionMutation] = useDeleteCollectionMutation();
  const [convertToSubscriptionMutation] = useMutation<
    ProgramsConvertToSubscriptionData,
    ConversionVars
  >(PROGRAMS_CONVERT_TO_SUBSCRIPTION);
  const [convertToUnassignedMutation] = useMutation<
    ProgramsConvertToUnassignedData,
    ConversionVars
  >(PROGRAMS_CONVERT_TO_UNASSIGNED);
  const { mutateAsync: duplicateCollection } = useDuplicateCollection();

  // ---- Dispatchers --------------------------------------------------------
  const reorder = useCallback(
    async ({ ids, salesType }: ReorderPayload): Promise<DispatcherResult> => {
      if (creatorId === null) {
        return { ok: false, message: 'Missing influencerId' };
      }
      const reqId = buildReqId(nowRef.current());
      setOptState((s) => startInflight(s, { reqId, salesType, ids }));
      try {
        await orderCollectionsMutation({
          variables: {
            influencerId: creatorId,
            salesType: toGqlSalesType(salesType),
            ids,
          },
        });
        setOptState((s) =>
          settleInflight(s, { reqId, salesType, result: { ok: true } }),
        );
        // Last-user-intent semantics: refresh the server order, then drop
        // this request's overlay so the user sees server-truth reflecting
        // the latest drag. `dropOverlayOnRefresh` is a no-op when a newer
        // reorder has already taken ownership of the bucket overlay, so a
        // mid-refetch second drag is not clobbered.
        const refresh = await silentRefetch();
        if ('message' in refresh) {
          return { ok: true, refreshFailed: refresh.message };
        }
        setOptState((s) => dropOverlayOnRefresh(s, { reqId, salesType }));
        return { ok: true };
      } catch (e) {
        const message = toErrorMessage(e, 'Reorder failed');
        setOptState((s) =>
          settleInflight(s, {
            reqId,
            salesType,
            result: { ok: false, message },
          }),
        );
        return { ok: false, message };
      }
    },
    [creatorId, orderCollectionsMutation, silentRefetch],
  );

  const archiveProgram = useCallback(
    async (collectionId: number): Promise<DispatcherResult> => {
      try {
        await archiveCollectionMutation({ variables: { collectionId } });
        return { ok: true, ...(await refreshAfterMutation()) };
      } catch (e) {
        return { ok: false, message: toErrorMessage(e, 'Archive failed') };
      }
    },
    [archiveCollectionMutation, refreshAfterMutation],
  );

  const deleteProgram = useCallback(
    async (collectionId: number): Promise<DispatcherResult> => {
      try {
        await deleteCollectionMutation({ variables: { collectionId } });
        return { ok: true, ...(await refreshAfterMutation()) };
      } catch (e) {
        return { ok: false, message: toErrorMessage(e, 'Delete failed') };
      }
    },
    [deleteCollectionMutation, refreshAfterMutation],
  );

  const publishProgram = useCallback(
    async (programId: number): Promise<DispatcherResult> => {
      try {
        await publishCollectionMutation({
          variables: { programId, onlyPublish: true },
        });
        return { ok: true, ...(await refreshAfterMutation()) };
      } catch (e) {
        return { ok: false, message: toErrorMessage(e, 'Publish failed') };
      }
    },
    [publishCollectionMutation, refreshAfterMutation],
  );

  const duplicateProgram = useCallback(
    async (programId: number): Promise<DuplicateDispatcherResult> => {
      if (creatorId === null) {
        return { ok: false, message: 'Missing influencerId' };
      }

      try {
        const duplicated = await duplicateCollection({
          collectionId: programId,
          businessModel: 'exclusive',
        });
        return {
          ok: true,
          duplicatedId: duplicated.id,
          refresh: refreshAfterMutation(),
        };
      } catch (e) {
        return { ok: false, message: toErrorMessage(e, 'Duplicate failed') };
      }
    },
    [creatorId, duplicateCollection, refreshAfterMutation],
  );

  const sellAsSubscription = useCallback(
    async (programId: number): Promise<DispatcherResult> => {
      try {
        await convertToSubscriptionMutation({ variables: { programId } });
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          message: toErrorMessage(e, 'Conversion failed'),
          code: toErrorCode(e),
        };
      }
    },
    [convertToSubscriptionMutation],
  );

  const removeFromSubscription = useCallback(
    async (programId: number): Promise<DispatcherResult> => {
      try {
        await convertToUnassignedMutation({ variables: { programId } });
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          message: toErrorMessage(e, 'Remove from subscription failed'),
        };
      }
    },
    [convertToUnassignedMutation],
  );

  // ---- Return -------------------------------------------------------------
  const buildFetchedModel = (domain: ProgramsDomain): Async<ProgramsModel> =>
    toAsyncFetched({
      creator: domain.creator,
      bundles: domain.bundles,
      programs: applyOptimisticOrder(
        sortByPersistedIndex(domain.programs),
        optState,
      ),
      inflight: optState.inflight,
      isCreatorLive,
      reorder,
      archiveProgram,
      deleteProgram,
      publishProgram,
      duplicateProgram,
      sellAsSubscription,
      removeFromSubscription,
    });

  if (!canFetch) return { type: 'not-fetched' };
  if (asyncDomain.type === 'fetched')
    return buildFetchedModel(asyncDomain.value);
  if (asyncDomain.type === 'fetching' && previousDomain !== null) {
    return buildFetchedModel(previousDomain);
  }
  return asyncDomain;
};

const sortByPersistedIndex = (
  programs: ReadonlyArray<ProgramItem>,
): Array<ProgramItem> =>
  [...programs].sort((a, b) => {
    const aIndex = a.index ?? Number.MAX_SAFE_INTEGER;
    const bIndex = b.index ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Parses and validates the influencerId param. Returns null when the value
 * is missing or non-numeric — callers treat null as "don't query yet".
 */
const toCreatorId = (influencerId: string | undefined): number | null => {
  if (!influencerId) return null;
  const n = Number(influencerId);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const toGqlSalesType = (salesType: ReorderPayload['salesType']): GqlSalesType =>
  salesType === 'subscription' ? GqlSalesType.Subscription : GqlSalesType.Otp;

/**
 * Normalizes Apollo and vanilla errors into a single user-safe message.
 * We prefer the first `graphQLErrors[0].message`, then `networkError`, then
 * any `Error.message`, falling back to a generic label.
 *
 * Exported for unit tests.
 */
export const toErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof ApolloError) {
    if (err.graphQLErrors && err.graphQLErrors[0]?.message) {
      return err.graphQLErrors[0].message;
    }
    if (err.networkError instanceof Error && err.networkError.message) {
      return err.networkError.message;
    }
    return err.message || fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

/**
 * Extracts the GraphQL error code (e.g. `COLLECTION_CONTENT_NOT_READY`) from
 * an Apollo error. The backend's `formatError` exposes it both as a top-level
 * `code` field and inside `extensions.code`, so we read both.
 */
export const toErrorCode = (err: unknown): string | undefined => {
  if (!(err instanceof ApolloError)) return undefined;
  const first = err.graphQLErrors?.[0] as
    | { code?: string; extensions?: { code?: string } }
    | undefined;
  return first?.code ?? first?.extensions?.code;
};
